import React, { useState } from 'react';
import { AlertCircle, CheckCircle, FileText } from 'lucide-react';

const EMRConsentForm = ({ patientName, onConsentSigned, onCancel }) => {
  const [formData, setFormData] = useState({
    fullName: patientName || '',
    agreedToTreatment: false,
    agreedToHipaa: false,
    agreedToPayment: false,
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Patient signature required";
    }
    
    if (!formData.agreedToTreatment) {
      newErrors.treatment = "Treatment consent required";
    }
    
    if (!formData.agreedToHipaa) {
      newErrors.hipaa = "HIPAA acknowledgment required";
    }
    
    if (!formData.agreedToPayment) {
      newErrors.payment = "Payment terms acknowledgment required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const consentRecord = {
        signature: formData.fullName,
        timestamp: new Date().toISOString(),
        treatment_consent: formData.agreedToTreatment,
        hipaa_acknowledgment: formData.agreedToHipaa,
        payment_terms: formData.agreedToPayment,
      };
      onConsentSigned(consentRecord);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-h-[80vh] overflow-y-auto">
      {/* Header */}
      <div className="border-b pb-4 mb-6">
        <div className="flex items-center text-blue-600 mb-2">
          <FileText className="w-8 h-8 mr-3" />
          <h2 className="text-2xl font-bold">Patient Consent & Agreement</h2>
        </div>
        <p className="text-gray-600">Required before treatment administration</p>
      </div>

      <div className="space-y-6">
        
        {/* Treatment Consent & Patient Rights */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
            Treatment Consent & Patient Rights
          </h3>
          <div className="text-gray-700 text-sm space-y-2 leading-relaxed">
            <p>
              <strong>Informed Consent:</strong> I authorize EliteIV Lounge to administer IV therapy as prescribed. I understand the treatment, benefits, and risks including infection, bruising, allergic reactions, and rare complications.
            </p>
            <p><strong>Patient Rights - I have the right to:</strong></p>
            <ul className="list-disc list-inside ml-3 space-y-1">
              <li>Receive respectful care</li>
              <li>Be informed of diagnosis and treatment</li>
              <li>Refuse treatment at any time</li>
              <li>Privacy and confidentiality</li>
              <li>Access my medical records</li>
              <li>Voice concerns without retaliation</li>
            </ul>
          </div>
          <div className="mt-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToTreatment}
                onChange={(e) => setFormData({...formData, agreedToTreatment: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-800">
                Patient agrees to treatment consent and acknowledges patient rights
              </span>
            </label>
            {errors.treatment && (
              <p className="text-red-600 text-xs mt-1 ml-7">{errors.treatment}</p>
            )}
          </div>
        </div>

        {/* HIPAA Privacy */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
            HIPAA Privacy Notice
          </h3>
          <div className="text-gray-700 text-sm space-y-2 leading-relaxed">
            <p>
              <strong>Privacy Practices:</strong> Your medical information may be used for treatment, payment, and healthcare operations.
            </p>
            <p><strong>Your rights:</strong> Request restrictions, inspect records, request amendments, receive accounting of disclosures, and file complaints.</p>
            <p><strong>Security:</strong> We maintain safeguards to protect your information and will not sell your data.</p>
          </div>
          <div className="mt-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToHipaa}
                onChange={(e) => setFormData({...formData, agreedToHipaa: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-800">
                Patient acknowledges receipt of HIPAA Privacy Notice
              </span>
            </label>
            {errors.hipaa && (
              <p className="text-red-600 text-xs mt-1 ml-7">{errors.hipaa}</p>
            )}
          </div>
        </div>

        {/* Payment Terms */}
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
            Payment Terms
          </h3>
          <div className="text-gray-700 text-sm space-y-2 leading-relaxed">
            <p><strong>Payment Policy:</strong> Payment due at time of service. Cash, credit, and debit accepted.</p>
            <p><strong>Financial Responsibility:</strong> I am responsible for all services rendered.</p>
            <p><strong>Memberships:</strong> Non-refundable, unused treatments expire per plan terms.</p>
            <p><strong>Cancellation:</strong> Less than 24 hours notice may incur fees.</p>
            <p><strong>Insurance:</strong> Services typically not covered. Full payment required regardless of insurance.</p>
          </div>
          <div className="mt-3">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.agreedToPayment}
                onChange={(e) => setFormData({...formData, agreedToPayment: e.target.checked})}
                className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm font-medium text-gray-800">
                Patient agrees to payment terms and financial responsibility
              </span>
            </label>
            {errors.payment && (
              <p className="text-red-600 text-xs mt-1 ml-7">{errors.payment}</p>
            )}
          </div>
        </div>

        {/* Signature */}
        <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Patient Signature</h3>
          <p className="text-gray-600 text-sm mb-3">
            By typing the patient's full name, staff confirms patient consent.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient's Full Name (Signature) *
            </label>
            <input
              type="text"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Patient's full legal name"
            />
            {errors.fullName && (
              <p className="text-red-600 text-xs mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1" />
                {errors.fullName}
              </p>
            )}
          </div>
          <p className="text-gray-500 text-xs mt-2">
            Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end mt-6 pt-4 border-t">
        <button
          onClick={onCancel}
          className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-md transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-md transition-colors"
        >
          Confirm Consent
        </button>
      </div>
    </div>
  );
};

export default EMRConsentForm;
